/**
 * Issues → Shipping tab container (BLU-732).
 *
 * Two shipping concerns share this tab, split into sub-tabs so neither buries
 * the other: the Portless-blocked list can run to hundreds of rows (the
 * recovery cutoff widened the sweep domain to the January backlog), and the
 * failures list — rarer but more urgent per item — must stay reachable without
 * scrolling past it.
 *
 * The tree shape is gated on the query's STATUS, not its count: while the
 * blocked read is in flight we show skeletons rather than mounting the
 * failures list bare and then yanking it into a sub-tab (which would destroy
 * its page/showResolved state and any open resolve dialog — on the HK-proxied
 * Ningbo link that in-flight window is seconds, not a frame).
 *
 * Sub-tab default: failures when any exist — they are the actionable,
 * per-item-urgent list; the blocked rows are explicitly self-clearing. A
 * healthy floor (nothing blocked) gets the failures list with no sub-tab
 * chrome at all, exactly as before BLU-713.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageX, Truck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useShippingFailures } from '@/features/packing-station/hooks/useShipping';
import { usePortlessBlocked } from '../hooks/usePortlessBlocked';
import { PortlessBlockedList } from './PortlessBlockedList';
import { ShippingFailuresList } from './ShippingFailuresList';

interface ShippingIssuesTabProps {
  locationId: string;
}

export function ShippingIssuesTab({ locationId }: ShippingIssuesTabProps) {
  const blockedQuery = usePortlessBlocked(locationId);

  if (blockedQuery.isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const blockedCount = blockedQuery.data?.length ?? 0;
  if (blockedCount === 0) {
    return <ShippingFailuresList locationId={locationId} />;
  }
  return <ShippingSubTabs locationId={locationId} blockedCount={blockedCount} />;
}

/**
 * Mounted only once the blocked query has resolved with rows, so the useState
 * initializer sees real counts and the default pane never moves out from
 * under the operator after first paint.
 */
function ShippingSubTabs({ locationId, blockedCount }: { locationId: string; blockedCount: number }) {
  const { t } = useTranslation('production');
  // Shared cache with the page-level badge read — no extra fetch, and for
  // coordinators the sidebar has already warmed it.
  const { data: shippingData } = useShippingFailures(locationId);
  const failuresCount = shippingData?.unresolvedCount ?? 0;
  const [subTab, setSubTab] = useState<'blocked' | 'failures'>(
    failuresCount > 0 ? 'failures' : 'blocked'
  );

  return (
    <Tabs value={subTab} onValueChange={(v) => setSubTab(v as 'blocked' | 'failures')}>
      <TabsList className="h-11">
        <TabsTrigger value="failures" className="gap-2 h-full">
          <Truck className="h-4 w-4" aria-hidden="true" />
          {t('issues.shipping.failuresSubTab')}
          {failuresCount > 0 && (
            <Badge variant="destructive" className="ml-1">
              {failuresCount}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="blocked" className="gap-2 h-full">
          <PackageX className="h-4 w-4" aria-hidden="true" />
          {t('issues.shipping.blockedSubTab')}
          <Badge variant="secondary" className="ml-1">
            {blockedCount}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="failures" className="mt-4">
        <ShippingFailuresList locationId={locationId} />
      </TabsContent>
      <TabsContent value="blocked" className="mt-4">
        <PortlessBlockedList locationId={locationId} />
      </TabsContent>
    </Tabs>
  );
}
